import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase/config';
import type { ID } from '@shared/types';

/**
 * 変更の「起点」。userId が「誰の権限で」なら、こちらは「何が引き起こしたか」。
 * 省略時（＝人が画面から操作した通常の変更）は監査ログに出力しない。
 * - 'ai-secretary'        AI秘書が起こした書込
 * - 'ai-review-confirm'   人がAIレビューを確定した操作（変更箇所パネルの「境界」になる）
 */
export type AuditSource = 'ai-secretary' | 'ai-review-confirm';

interface WriteAuditLogParams {
  entityType: string;
  entityId: ID;
  action: 'create' | 'update' | 'delete' | 'read';
  userId: ID;
  before?: unknown;
  after?: unknown;
}

/**
 * 実行中の由来。writeAuditLog はサービス層の内部（createQuote 等）から呼ばれるため、
 * 呼び出し側から source を引数で渡す経路が無い（174箇所・49ファイル）。
 * 全サービスのシグネチャを変えずに由来を残す唯一の手段として、実行スコープで伝える。
 *
 * 【前提】ブラウザは単一スレッドで、業務データの書込はユーザー操作起点で直列に起きる。
 * withAuditSource の await 中に走る書込は、その fn が引き起こしたもの
 * （例: createInvoice → syncOrderInvoiceTotal による受注の更新）なので、
 * 連鎖分にも同じ由来が付くのが正しい。
 */
let currentSource: AuditSource | null = null;

/**
 * fn の実行中に発生した監査ログに、変更の起点を刻む。
 * AI秘書の確定処理（人間が確認カードで「実行」を押したとき）はこれで包む。
 *
 *   await withAuditSource('ai-secretary', () => createQuote(data, uid));
 *
 * 例外が起きても finally で必ず元に戻すため、由来が漏れ出して
 * 後続の通常操作を汚染することはない。
 */
export async function withAuditSource<T>(source: AuditSource, fn: () => Promise<T>): Promise<T> {
  const previous = currentSource;
  currentSource = source;
  try {
    return await fn();
  } finally {
    currentSource = previous;
  }
}

export async function writeAuditLog(params: WriteAuditLogParams): Promise<void> {
  await addDoc(collection(db, 'audit_logs'), {
    entityType: params.entityType,
    entityId: params.entityId,
    action: params.action,
    userId: params.userId,
    before: params.before ?? null,
    after: params.after ?? null,
    timestamp: serverTimestamp(),
    // 通常操作ではフィールド自体を出さない（既存ログの形を変えない）
    ...(currentSource ? { source: currentSource } : {}),
  });
}
